import { CheckIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { patchApplicationForm } from '@/api/Rover/ApplicationForm';
import type { IPatchApplicationFormServicePayload } from '@/api/Rover/ApplicationForm/types';
import FileInputField from '@/components/UI/Form/FileInputFied';
import InputField from '@/components/UI/Form/InputField';
import ReactSelect from '@/components/UI/Form/ReactSelect';
import { Textarea } from '@/components/UI/Form/TextArea';
import { RoverReactQueryKey } from '@/constants/ReactQueryKeys';
import useUpload from '@/hooks/useUpload';
import { notifyUser } from '@/store/Toast';
import type { ReactSelectOption } from '@/types/Constants';
import { sanitizeResponseError } from '@/utils';

interface IEditableItemProps {
  id?: string;
  name: string;
  value: any;
  defaultValue?: string | number | ReactSelectOption | undefined;
  inputType?: 'text' | 'textarea' | 'select' | 'file' | 'multiselect';
  options?: ReactSelectOption[];
  validationSchema?: any;

  toggleEdit: (key: string, action?: 'save' | 'cancel' | 'edit') => void;
}

export function EditableInput({
  id,
  name,
  value,
  defaultValue,
  validationSchema,
  toggleEdit,
  options,
  inputType = 'text',
}: IEditableItemProps) {
  const { applicationFormId } = useParams<string>();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const {
    register,
    setValue,
    watch,
    getValues,
    formState: { errors, ...rest },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: { [name]: defaultValue || value },
    mode: 'all',
  });

  const uploadApplicationLogo = useUpload();
  const logo: any = watch(name);

  const updateApplicationForm = useMutation(
    (data: Partial<IPatchApplicationFormServicePayload>) =>
      patchApplicationForm({
        data,
        applicationFormId: applicationFormId!,
      }),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries([RoverReactQueryKey.GET_APPLICATION_FORM_DETAILS]);
        toggleEdit(name);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: data.message,
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const handleSave = (key: string) => {
    const data: any = { [key]: getValues(key) };

    if (value === getValues(key)) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'No changes were detected to update.',
          toastType: 'info',
        })
      );
      toggleEdit(key);
      return;
    }

    if (data.logo) {
      uploadApplicationLogo.uploadFile({
        file: data?.logo[0],
        resource: 'applicationForm',
        resourceId: applicationFormId!,
        path: 'thumbnails',
        applyData: (signedFiles: any) => {
          const assetsFile = {
            name: data.logo[0].name,
            key: signedFiles.key,
            contentType: signedFiles.type,
            assetType: 'image',
            size: signedFiles.size,
          };
          updateApplicationForm.mutate({ logo: assetsFile });
          setValue('logo', '');
        },
      });
    }

    if (!Object.hasOwn(errors, key) && !data.logo) {
      updateApplicationForm.mutate(data);
    }
  };

  return (
    <dd className='mt-1 text-sm text-gray-900 flex items-center gap-2'>
      <div className='w-1/2'>
        {inputType === 'text' && <InputField name={name} type={inputType} error={errors[name]?.message} register={register} />}
        {inputType === 'select' && (
          <ReactSelect
            id={id}
            name={name}
            options={options}
            placeholder='Select'
            defaultValue={defaultValue}
            onChange={(option) => {
              const val = option as ReactSelectOption;
              setValue(name, val.value);
            }}
          />
        )}
        {inputType === 'multiselect' && (
          <ReactSelect
            id={id}
            name={name}
            options={options}
            isMulti
            defaultValue={defaultValue}
            placeholder='Select Viewers'
            onChange={(option) => {
              const val: number[] = (option as ReactSelectOption[])?.map((d: ReactSelectOption) => Number(d?.value));
              setValue(name, val);
            }}
          />
        )}
        {inputType === 'textarea' && <Textarea error={errors[name]?.message?.toString()} {...register(name)} />}
        {inputType === 'file' && (
          <FileInputField
            name={name}
            value={logo?.[0]?.name}
            error={errors[name]?.message?.toString()}
            register={register}
            accept='.jpg, .jpeg, .png, .svg'
          />
        )}
      </div>
      <div className='flex gap-2'>
        <span
          className='p-1 bg-neutralColor-lightest rounded-full text-primaryColor-normal hover:bg-neutralColor-lighter cursor-pointer'
          onClick={() => handleSave(name)}
        >
          <CheckIcon className='h-5 w-5' />
        </span>
        <span
          className='p-1 bg-neutralColor-lightest rounded-full text-tertiaryColor-normal hover:bg-neutralColor-lighter cursor-pointer'
          onClick={() => toggleEdit(name, 'cancel')}
        >
          <XCircleIcon className='h-5 w-5' />
        </span>
      </div>
    </dd>
  );
}
